<template>
  <auth-guard
    route
  >
    <auth-guard
      can="api.view_hedge,api.view_lot,api.view_yard"
    >
      <v-alert
        class="d-print-none"
        type="warning"
        color="warning"
        outlined
      >
        Values are actively re-calculated on this page, to ensure values are properly reflected on the print page unlock and save.
      </v-alert>
      <brokered-settlement-skeleton
        v-if="loading"
        title="Client Proforma"
      />
      <ValidationObserver
        v-else
        v-slot="{ invalid, reset }"
      >
        <v-form
          :readonly="isLocked"
          :value="invalid"
          class="mb-16"
        >
          <v-container>
            <url-breadcrumbs
              v-if="breadcrumbs"
              :items="breadcrumbs"
            />
            <h1>
              Client Proforma
            </h1>
            <div class="caption">
              Generates a detailed report for clients showing returns on a lot after it has been processed at the smelter, providing transparency and financial insight.
            </div>
            <v-toolbar
              flat
              dense
              color="transparent"
            >
              <v-btn
                :color="isLocked ? 'error' : ''"
                small
                rounded
                @click="$store.commit('clientProformaStore/setIsLocked', !isLocked)"
              >
                {{ isLocked ? 'Locked' : 'unlocked' }}
                <v-icon
                  small
                  class="ml-2"
                >
                  {{ isLocked ? 'mdi-lock-outline' : 'mdi-lock-open-outline' }}
                </v-icon>
              </v-btn>
              <v-spacer />
              <info-tooltip>
                <template v-slot:wrap-me>
                  <v-btn
                    :disabled="isLocked"
                    text
                    @click="refreshData"
                  >
                    Refresh Data
                  </v-btn>
                </template>
                Replace values with the latest lot data.
              </info-tooltip>
              <v-btn
                :disabled="!advanceCalculatorHistory.length"
                text
                class="ml-2"
                target="_blank"
                :to="{ name: 'client-proforma-print', params: { id: $route.params.id } }"
              >
                Print
              </v-btn>
            </v-toolbar>
            <v-select
              :value="historyVersion"
              label="Revision History"
              :items="advanceCalculatorHistory"
              :item-text="historyLabelFormat"
              item-value="id"
              @change="setVersionFromHistoryById"
            />
            <div>
              <h2>
                Properties
              </h2>
              <v-sheet class="pa-4">
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      v-model="lotName"
                      readonly
                      class="dense-filled"
                      label="Lot Name"
                      validation-rules="required|max:400"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <ValidationProvider
                      immediate
                      rules="required"
                      v-slot="{ errors }"
                    >
                      <v-select
                        label="Yard"
                        :value="yardId"
                        :items="$store.state['clientProformaStore'].yardChoices"
                        :item-text="x => `${x.id} - ${x.name} ${x.address || ''}, ${x.city || ''} ${x.state || ''}`"
                        item-value="id"
                        :error-messages="errors"
                        @change="$store.commit('clientProformaStore/setYardId', $event)"
                      />
                    </ValidationProvider>
                    <v-alert
                      v-if="!yardId"
                      dense
                      text
                      type="warning"
                      class="mt-2"
                    >
                      Select a Yard to load Brokerage Terms and calculate rates.
                    </v-alert>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-date
                      label="Est. Ship Date"
                      :value="shipDate"
                      :show-date-picker="!isLocked"
                      @input="$store.commit('clientProformaStore/setShipDate', $event)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-text-field
                      label="Customer Reference"
                      :value="customerReference"
                      @input="$store.commit('clientProformaStore/setCustomerReference', $event)"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                    offset-md="4"
                  >
                    <rules-date
                      :value="arrivalDate"
                      label="Arrival Date"
                      :show-date-picker="!isLocked"
                      @input="$store.commit('clientProformaStore/setArrivalDate', $event)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      validation-rules="required|max_value:2147483647|min_value:0|decimal:0"
                      :value="convertersProcessed"
                      label="Converters Processed"
                      persistent-hint
                      hint="Uses the total from check ins"
                      type="number"
                      @change="$store.commit('clientProformaStore/setConvertersProcessed', $event)"
                    />
                    <v-checkbox
                      :value="printPieces"
                      class="pt-0 mt-0"
                      label="Print Pieces"
                      @change="$store.commit('clientProformaStore/setPrintPieces', $event)"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      validation-rules="required|max_value:999999999|min_value:0|decimal:3"
                      :value="mixedCatalystWeight"
                      label="Mixed Catalyst Weight/Received Lbs"
                      type="number"
                      @change="$store.commit('clientProformaStore/setMixedCatalystWeight', $event)"
                    />
                    <v-checkbox
                      :value="printWeight"
                      class="pt-0 mt-0"
                      label="Print Weight"
                      @change="$store.commit('clientProformaStore/setPrintWeight', $event)"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-date
                      :value="clientValueDate"
                      :readonly="clientValueDateAuto"
                      label="Client Value Date"
                      :show-date-picker="!isLocked"
                      @input="$store.commit('clientProformaStore/setClientValueDate', $event)"
                    />
                    <div class="text-center text-sm-body-2">
                      <v-switch
                        v-if="!isLocked"
                        v-model="clientValueDateAuto"
                        :label="`Use Arrival + Client Payment Days (${getTermsClientPaymentDays}})`"
                      />
                    </div>
                  </v-col>
                </v-row>
              </v-sheet>
              <div class="py-4" />
              <h2>
                Outright Units
              </h2>
              If the units are already paid, the are not included in the deposit calculation
              <v-sheet class="pa-4 mb-6">
                <v-simple-table>
                  <thead>
                    <tr>
                      <th
                        v-for="h in ['Type', 'Quantity', 'Unit Price', 'Total Price', 'Mark Paid']"
                        :key="h"
                        :style="'min-width:' + (['Type', 'Mark Paid'].includes(h)) ? 50 : 100 + 'px;'"
                        v-text="h"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="outright in $store.state['clientProformaStore'].purchasedOutright"
                      :key="outright.materialType"
                    >
                      <td>
                        <strong>{{ outright.materialType }}</strong>
                      </td>
                      <td>
                        <rules-text
                          validation-rules="required|max_value:2147483647|min_value:0|decimal:0"
                          :value="outright.quantity"
                          type="number"
                          @input="updateOutrightValue(outright.materialType, 'quantity', $event)"
                        />
                      </td>
                      <td>
                        <rules-text
                          validation-rules="required|max_value:9999999999|min_value:0|decimal:2"
                          :value="outright.price"
                          type="number"
                          prepend-icon="mdi-currency-usd"
                          @input="updateOutrightValue(outright.materialType, 'price', $event)"
                        />
                      </td>
                      <td class="pt-2">
                        <rules-text
                          dense
                          class="dense-filled"
                          readonly
                          validation-rules="required|max_value:9999999999|min_value:0|decimal:2"
                          :value="outright.totalPrice"
                          type="number"
                          prepend-icon="mdi-currency-usd"
                        />
                      </td>
                      <td class="pb-4 pl-6">
                        <v-simple-checkbox
                          :value="outright.paid"
                          color="primary"
                          @input="updateOutrightValue(outright.materialType, 'paid', $event)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <v-row>
                  <v-col
                    cols="12"
                    md="9"
                  >
                    <v-select
                      v-model="unitCode"
                      class="d-inline-block mr-4"
                      :items="availableCodes"
                    />
                    <v-btn
                      :disabled="!unitCode"
                      outlined
                      small
                      @click="() => addPurchasedOutrightCode(unitCode)"
                    >
                      Add Line
                    </v-btn>
                  </v-col>
                  <v-col
                    cols="12"
                    md="3"
                  >
                    <rules-text
                      class="dense-filled"
                      :value="getGrossOutrightUnpaidValue"
                      label="Gross Unpaid Value"
                      prepend-icon="mdi-currency-usd"
                      persistent-hint
                      hint="Sum of Unpaid Outright"
                    />
                  </v-col>
                </v-row>
              </v-sheet>
              <div class="py-4" />
              <h2>
                Metal Allocations
              </h2>
              <v-sheet class="pa-4">
                <span class="h2">Expected Return Values for Lot</span>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-select
                      :value="ouncePayoutType"
                      :readonly="ouncePayoutTypeAuto"
                      label="Ounce Payout Basis"
                      :items="$store.state['clientProformaStore'].ouncePayoutOptions"
                      @change="setOuncePayoutType"
                    />
                    <div class="text-center text-sm-body-2">
                      <v-switch
                        v-if="!isLocked"
                        v-model="ouncePayoutTypeAuto"
                        label="Use Payout Type from Terms"
                      />
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="containedPt"
                      type="number"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Contained Pt"
                      @change="$store.commit('clientProformaStore/setContainedPt', $event)"
                    />
                    <rules-text
                      :value="brokeredRatePt"
                      type="number"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Brokered Rate Pt"
                      @change="$store.commit('clientProformaStore/setBrokeredRatePt', $event)"
                    />
                    <rules-text
                      v-model="getAvailablePt"
                      readonly
                      type="number"
                      class="dense-filled"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Available Pt"
                    />
                    <v-progress-linear
                      :value="getAllocatedPt / getAvailablePt * 100"
                      height="24"
                      color="#0068b9"
                      rounded
                    >
                      <span class="font-weight-medium white--text pl-2">
                        {{ getAllocatedPt }} / {{ getAvailablePt }} rtoz
                      </span>
                      <v-spacer />
                      <span class="font-weight-medium white--text pr-2">
                        {{ getUnallocatedPt }}
                      </span>
                    </v-progress-linear>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="containedPd"
                      type="number"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Contained Pd"
                      @change="$store.commit('clientProformaStore/setContainedPd', $event)"
                    />
                    <rules-text
                      :value="brokeredRatePd"
                      type="number"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Brokered Rate Pd"
                      @change="$store.commit('clientProformaStore/setBrokeredRatePd', $event)"
                    />
                    <rules-text
                      v-model="getAvailablePd"
                      readonly
                      type="number"
                      class="dense-filled"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Available Pd"
                    />
                    <v-progress-linear
                      height="24"
                      :value="getAllocatedPd / getAvailablePd * 100"
                      color="#d00e00"
                      rounded
                    >
                      <span class="font-weight-medium white--text pl-2">
                        {{ getAllocatedPd }} / {{ getAvailablePd }} rtoz
                      </span>
                      <v-spacer />
                      <span class="font-weight-medium white--text pr-2">
                        {{ getUnallocatedPd }}
                      </span>
                    </v-progress-linear>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="containedRh"
                      type="number"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Contained Rh"
                      @change="$store.commit('clientProformaStore/setContainedRh', $event)"
                    />
                    <rules-text
                      :value="brokeredRateRh"
                      type="number"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Brokered Rate Rh"
                      @change="$store.commit('clientProformaStore/setBrokeredRateRh', $event)"
                    />
                    <rules-text
                      v-model="getAvailableRh"
                      readonly
                      type="number"
                      class="dense-filled"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Available Rh"
                    />
                    <v-progress-linear
                      height="24"
                      :value="getAllocatedRh / getAvailableRh * 100"
                      color="#006104"
                      rounded
                    >
                      <span class="font-weight-medium white--text pl-2">
                        {{ getAllocatedRh }} / {{ getAvailableRh }} rtoz
                      </span>
                      <v-spacer />
                      <span class="font-weight-medium white--text pr-2">
                        {{ getUnallocatedRh }}
                      </span>
                    </v-progress-linear>
                  </v-col>
                </v-row>
                <div
                  v-for="allocation in allocations"
                  :key="allocation.hedgeId"
                >
                  <div class="d-flex justify-space-between align-center">
                    <div class="text-h6">
                      Hedge #{{ allocation.hedgeId }}
                    </div>
                    <v-btn
                      :disabled="isLocked"
                      outlined
                      x-small
                      @click="removeAllocation(allocation)"
                    >
                      Remove
                    </v-btn>
                  </div>
                  <v-row>
                    <v-col>
                      <edit-metal
                        alternate-style
                        :hedge="allocation"
                        @setHedge="updateAllocations(allocation.hedgeId, $event)"
                      />
                    </v-col>
                  </v-row>
                </div>
                <div class="mt-8">
                  <div class="text-h6">
                    Add Hedges
                  </div>
                  <div class="text-caption">
                    Hedge Ounces Basis: {{ brokerageTerms.hedge_ounces_text }} &mdash;
                    <span v-if="brokerageTerms.hedge_ounces === 'CO'">
                      using 100 / 100 / 100 (Client Ounces)
                    </span>
                    <span v-else>
                      using term rates {{ brokerageTerms.pt_rate }} / {{ brokerageTerms.pd_rate }} / {{ brokerageTerms.rh_rate }} (Pt / Pd / Rh)
                    </span>
                  </div>
                  <div class="d-flex align-center">
                    <search-autocomplete
                      v-model="selectedHedge"
                      class="mx-4"
                      label="Search for Hedges"
                      item-text="friendlyName"
                      api-path="/api/hedges/"
                      :params="{ ordering: '-id' }"
                      :format-entry="x => Object.assign({}, { friendlyName: `Hedge #${x.id} ${x.pt_available_toz}@$${x.pt_rate_per_toz_fx} / ${x.pd_available_toz}@$${x.pd_rate_per_toz_fx} / ${x.rh_available_toz}@$${x.rh_rate_per_toz_fx}` }, x)"
                    />
                    <v-btn
                      class="mr-4"
                      color="primary"
                      outlined
                      x-small
                      @click="addSearchedHedge(selectedHedge)"
                    >
                      Add
                    </v-btn>
                  </div>
                  <v-list>
                    <v-list-item
                      v-for="hedge in getUnallocatedHedges"
                      :key="hedge.id"
                    >
                      <v-list-item-content>
                        {{
                          `Hedge #${hedge.id} ${hedge.ptAvailableToz}@${hedge.ptRate} / ${hedge.pdAvailableToz}@${hedge.pdRate} / ${hedge.rhAvailableToz}@${hedge.rhRate}`
                        }}
                        <span class="text-caption">designated (Uses Alternate Pricing)</span>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn
                          :disabled="isLocked"
                          color="accent"
                          outlined
                          x-small
                          @click="addHedge(hedge)"
                        >
                          Add
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider />
                    <v-list-item>
                      <v-list-item-content>
                        Add Live Hedge
                        <span class="text-caption">covers the unallocated amount of troy oz</span>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn
                          :disabled="isLocked"
                          outlined
                          x-small
                          @click="addBlankHedge"
                        >
                          Add
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        Select from Lot Allocations
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn
                          :disabled="isLocked || !lotId"
                          outlined
                          x-small
                          @click="addMetalDialog = !addMetalDialog"
                        >
                          Allocated Hedges
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </div>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="getAllocatedReturnablePt"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Total Returnable Pt rtoz"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="getAllocatedReturnablePd"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Total Returnable Pd rtoz"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="getAllocatedReturnableRh"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Total Returnable Rh rtoz"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="getSummaryReturnablePtToz"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Summary Pt rtoz"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="getSummaryReturnablePdToz"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Summary Pd rtoz"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="getSummaryReturnableRhToz"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Summary Rh rtoz"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="getInternalPtToz"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Internal Pt Balance"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="getInternalPdToz"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Internal Pd Balance"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="getInternalRhToz"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Internal Rh Balance"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    offset-md="9"
                    md="3"
                  >
                    <rules-text
                      class="dense-filled"
                      :value="getGrossMetalValue"
                      label="Gross Metal Value"
                      prepend-icon="mdi-currency-usd"
                      persistent-hint
                      hint="Sum Hedge Values"
                    />
                  </v-col>
                </v-row>
              </v-sheet>
            </div>
            <div class="py-4" />
            <h2>
              Fees
            </h2>
            <v-sheet class="pa-4">
              <v-row>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="refiningFee"
                    :readonly="refiningFeeAuto"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Refining Fee"
                    prepend-icon="mdi-currency-usd"
                    @change="$store.commit('clientProformaStore/setRefiningFee', $event)"
                  />
                  <div class="text-center text-sm-body-2">
                    <v-switch
                      v-if="!isLocked"
                      v-model="refiningFeeAuto"
                      :label="`Use fee from terms ${getTermsRefiningFeeTotal}`"
                    />
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="processingFee"
                    :readonly="processingFeeAuto"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Processing Fee"
                    prepend-icon="mdi-currency-usd"
                    @change="$store.commit('clientProformaStore/setProcessingFee', $event)"
                  />
                  <div class="text-center text-sm-body-2">
                    <v-switch
                      v-if="!isLocked"
                      v-model="processingFeeAuto"
                      :label="`Use value from terms ${getTermsTreatmentCharge}`"
                    />
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="handlingFee"
                    :readonly="handlingFeeAuto"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Handling Fee"
                    prepend-icon="mdi-currency-usd"
                    persistent-hint
                    :hint="`A term fee of ${brokerageTerms.handling_fee_fx} applies to loads under 2000 lbs`"
                    @change="$store.commit('clientProformaStore/setHandlingFee', $event)"
                  />
                  <div class="text-center text-sm-body-2">
                    <v-switch
                      v-if="!isLocked"
                      v-model="handlingFeeAuto"
                      :label="`Use fee from terms ${getTermsHandlingFee}`"
                    />
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="freightCharge"
                    :readonly="getFreightChargeResponsibilityPGM"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Freight Charge"
                    persistent-hint
                    hint="Users total from allocated transits."
                    prepend-icon="mdi-currency-usd"
                    @change="$store.commit('clientProformaStore/setFreightCharge', $event)"
                  />
                  <div class="text-center text-sm-body-2">
                    <span v-if="getFreightChargeResponsibilityPGM">
                      Freight Charge is PGM Responsibility
                    </span>
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="assayFee"
                    :readonly="assayFeeAuto"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Assay Fee"
                    prepend-icon="mdi-currency-usd"
                    @change="$store.commit('clientProformaStore/setAssayFee', $event)"
                  />
                  <div class="text-center text-sm-body-2">
                    <v-switch
                      v-if="!isLocked"
                      v-model="assayFeeAuto"
                      :label="`Use fee from terms ${getTermAssayFee}`"
                    />
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="shreddingFee"
                    :readonly="shreddingFeeAuto"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Shredding Fee"
                    prepend-icon="mdi-currency-usd"
                    @change="$store.commit('clientProformaStore/setShreddingFee', $event)"
                  />
                  <div class="text-center text-sm-body-2">
                    <v-switch
                      v-if="!isLocked"
                      v-model="shreddingFeeAuto"
                      :label="`Use fee from terms ${getTermShreddingFee}`"
                    />
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="netOtherFees"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Net Other Fees"
                    prepend-icon="mdi-currency-usd"
                    @change="$store.commit('clientProformaStore/setNetOtherFees', $event)"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-checkbox
                    :value="consolidateFees"
                    class="px-4"
                    label="Consolidate Fees When Printing"
                    @change="$store.commit('clientProformaStore/setConsolidateFees', $event)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    v-model="getTotalFees"
                    readonly
                    class="dense-filled"
                    type="number"
                    label="Total Fees"
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="getNetValue"
                    readonly
                    class="dense-filled"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Net Value"
                    prepend-icon="mdi-currency-usd"
                    persistent-hint
                    hint="Unpaid Value + Metal Value - Fees"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="3"
                  offset-md="6"
                >
                  <rules-text
                    :value="brokerageFee"
                    :readonly="brokerageFeeAuto"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Brokerage Fee"
                    prepend-icon="mdi-currency-usd"
                    @change="$store.commit('clientProformaStore/setBrokerageFee', $event)"
                  />
                  <v-switch
                    v-if="!isLocked"
                    v-model="brokerageFeeAuto"
                    x-small
                    :label="`Use fee from terms ${getTermBrokerageFee}`"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="getNetValueLessBrokerage"
                    readonly
                    class="dense-filled"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Net Value (Less Brokerage Fee)"
                    prepend-icon="mdi-currency-usd"
                    persistent-hint
                    hint="Unpaid Value + Metal Value - Fees"
                  />
                </v-col>
              </v-row>
            </v-sheet>
            <div class="py-4" />
            <h2>
              Brokerage Terms
            </h2>
            Terms are displayed for reference only
            <v-sheet>
              <v-simple-table>
                <tbody>
                  <tr
                    v-for="term in getCleanedBrokerageTerms"
                    :key="term[0]"
                  >
                    <th>{{ term[0] }}</th>
                    <td
                      class="text-right"
                    >
                      {{ term[1] }}
                    </td>
                    <td
                      class="text-right"
                    >
                      {{ term[2] }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-sheet>
            <div class="py-4" />
            <h2>
              Finance / Amount Due
            </h2>
            <v-sheet class="pa-4">
              <v-row>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-date
                    :value="expectedPaymentDate"
                    label="Expected Payment Date"
                    prepend-icon="mdi-calendar"
                    :show-date-picker="!isLocked"
                    @input="$store.commit('clientProformaStore/setExpectedPaymentDate', $event)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    readonly
                    class="dense-filled"
                    v-model="getDaysOfInterest"
                    label="Days of Interest"
                    type="number"
                    persistent-hint
                    hint="Client Value Date - Expected Payment Date"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="interestPercent"
                    :readonly="interestRateAuto"
                    label="Interest Rate %"
                    type="number"
                    @change="$store.commit('clientProformaStore/setInterestPercent', $event)"
                  />
                  <div class="text-center text-sm-body-2">
                    <v-switch
                      v-if="!isLocked"
                      v-model="interestRateAuto"
                      label="Use Term Rate"
                    />
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    v-model="getFinanceCharge"
                    class="dense-filled"
                    readonly
                    label="Finance Charge"
                    type="number"
                    persistent-hint
                    hint="(Net Value - Advances) * Interest Percent / 360 * Days of Interest"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  offset-md="9"
                  md="3"
                >
                  <rules-text
                    :value="getSettlementAmountDue"
                    readonly
                    class="dense-filled"
                    label="Settlement Amount Due"
                    type="number"
                    hint="Net Value + Finance Charge"
                  />
                  <rules-text
                    :value="totalAdvances"
                    readonly
                    class="dense-filled"
                    label="Advance Payment"
                    type="number"
                    hint="Does not reflect Advance Interest"
                    persistent-hint
                  />
                  <rules-text
                    :value="settlementDue"
                    readonly
                    class="dense-filled"
                    label="Settlement Due"
                    type="number"
                    hint="Settlement Amount Due less Advances"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    v-model="getDepositTotal"
                    class="dense-filled"
                    readonly
                    label="Deposit Total"
                    type="number"
                    hint="Interest Calculated combined with Advance Interest"
                  />
                </v-col>
              </v-row>
            </v-sheet>
            <div class="py-4" />
            <h2>
              Advances
            </h2>
            Advances will be saved separately to the advance calculator, and will be consistent across revisions.
            <v-sheet class="pa-4">
              <v-row
                v-for="advance in getAdvances"
                :key="advance.id"
              >
                <v-col
                  cols="12"
                  md="9"
                  class="mt-8"
                >
                  <router-link-guard
                    :to="{ name: 'advance-view', params: { id: advance.id } }"
                    target="_blank"
                  >
                    Advance #{{ advance.id }}
                  </router-link-guard>
                  <br>
                  {{ advance.payment_date | formatDate }}
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    readonly
                    class="dense-filled"
                    :label="`Advance #${ advance.id } Amount`"
                    :value="advance.amount_fx"
                    :hint="`Source ${advance.currency} ${advance.amount}`"
                  />
                  <v-text-field
                    readonly
                    :label="`Interest (${advance.interestDays} days)`"
                    class="dense-filled"
                    :value="advance.interest"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="9"
                >
                  Recommended Advance is {{ getRecommendedAdvance }}. Click add advance to add advance entry.
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                  class="text-right"
                >
                  <auth-guard
                    can="api.add_advance"
                    type="component"
                  >
                    <v-btn
                      text
                      color="primary"
                      rounded
                      small
                      :to="{
                        name: 'advance-create',
                        query: advanceQuery
                      }"
                    >
                      Add Advance
                      <v-icon>mdi-open-in-new</v-icon>
                    </v-btn>
                  </auth-guard>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  offset-md="9"
                  md="3"
                >
                  <rules-text
                    readonly
                    class="dense-filled"
                    label="Total Advances"
                    :value="totalAdvances"
                  />
                  <v-text-field
                    readonly
                    label="Total Interest"
                    class="dense-filled"
                    :value="totalAdvancesInterest"
                  />
                </v-col>
              </v-row>
            </v-sheet>
          </v-container>
          <text-fab
            v-if="!isLocked"
            bottom
            right
            fixed
            icon-text="mdi-content-save-outline"
            :color="isModified ? 'primary': ''"
            @click="save"
          >
            Save Changes
          </text-fab>
        </v-form>
      </ValidationObserver>
      <add-from-allocations
        v-model="addMetalDialog"
        v-if="lotId"
        :lot-id="lotId"
        @addHedge="addHedge"
      />
    </auth-guard>
  </auth-guard>
</template>

<script>

import BrokeredSettlementSkeleton from '@/views/Lots/components/Brokered/components/BrokeredSettlementSkeleton.vue'
import RulesText from '@/components/ValidationFields/RulesText.vue'
import RulesDate from '@/components/ValidationFields/RulesDate.vue'
import EditMetal from '@/views/Lots/components/Brokered/components/EditMetal.vue'
import moment from 'moment'
import SearchAutocomplete from '@/base/components/SearchAutocomplete.vue'
import { mapGetters, mapState } from 'vuex'
import AddFromAllocations from './AddFromAllocations.vue'
import lot from '../Lot.vue'

export default {
  name: 'ClientProforma',
  components: {
    AddFromAllocations,
    SearchAutocomplete,
    EditMetal,
    RulesDate,
    RulesText,
    BrokeredSettlementSkeleton
  },
  data () {
    return {
      isModified: false,
      selectedHedge: null,
      breadcrumbs: [
        ['lot list', { name: 'lot-list' }],
        [`Lot #${this.$route.params.id}`, { name: 'lot' }],
        [`Client Proforma`, { name: 'client-proforma' }]
      ],
      yardChoices: [],
      loading: false,
      readonly: true,
      unitCode: null,
      addMetalDialog: false
    }
  },
  watch: {
    yardId () {
      this.$store.dispatch('clientProformaStore/fetchYardTerms')
    },
    snapshot () {
      this.isModified = true
    },
    // The following are computed terms, that will automatically update stored values unless blocked
    getClientValueDateByArrival (value) {
      if (!this.clientValueDateAuto) return
      this.$store.commit('clientProformaStore/setClientValueDate', value)
    },
    getTermsRefiningFeeTotal (value) {
      if (!this.refiningFeeAuto) return
      this.$store.commit('clientProformaStore/setRefiningFee', value)
    },
    getTermsTreatmentCharge (value) {
      if (!this.processingFeeAuto) return
      this.$store.commit('clientProformaStore/setProcessingFee', value)
    },
    getTermsHandlingFee (value) {
      if (!this.handlingFeeAuto) return
      this.$store.commit('clientProformaStore/setHandlingFee', value)
    },
    getTermAssayFee (value) {
      if (!this.assayFeeAuto) return
      this.$store.commit('clientProformaStore/setAssayFee', value)
    },
    getTermShreddingFee (value) {
      if (!this.shreddingFeeAuto) return
      this.$store.commit('clientProformaStore/setShreddingFee', value)
    },
    getTermBrokerageFee (value) {
      if (!this.brokerageFeeAuto) return
      this.$store.commit('clientProformaStore/setBrokerageFee', value)
    },
    getTermsPayoutType (value) {
      if (!this.ouncePayoutTypeAuto) return
      this.$store.commit('clientProformaStore/setOuncePayoutType', value?.value)
    },
    getTermInterestRate (value) {
      if (!this.interestRateAuto) return
      this.$store.commit('clientProformaStore/setInterestPercent', value)
    },
    getFreightChargeResponsibilityPGM (value) {
      this.updateValues()
    },
    isLocked (value) {
      if (!value) this.updateValues()
    }
  },
  computed: {
    ...mapState('clientProformaStore', [
      'brokerageTerms'
    ]),
    lot () {
      return lot
    },
    availableCodes () {
      const outright = this.$store.state['clientProformaStore'].purchasedOutright.map(x => x.materialType)
      return this.$store.getters['getConverterTypes'].map(x => x.value).filter(x => !outright.includes(x))
    },
    advanceQuery () {
      return {
        lot: this.lotId,
        amount: this.getRecommendedAdvance,
        redirect: JSON.stringify({
          name: 'client-proforma',
          params: { id: this.lotId }
        })
      }
    },
    snapshot () {
      return [
        this.$store.getters['clientProformaStore/getStateSnapshot']
      ]
    },
    allocations () {
      return this.$store.getters['clientProformaStore/getAllocations'].map(allocation => ({
        hedgeId: allocation.hedgeId,
        pt_returnable: allocation.ptToz,
        pt_return_rate: allocation.ptRate,
        pt_client_returnable: allocation.ptRtoz,
        pt_mk: allocation.ptMarketRate,
        pt_value: allocation.ptValue,
        pd_returnable: allocation.pdToz,
        pd_return_rate: allocation.pdRate,
        pd_client_returnable: allocation.pdRtoz,
        pd_mk: allocation.pdMarketRate,
        pd_value: allocation.pdValue,
        rh_returnable: allocation.rhToz,
        rh_return_rate: allocation.rhRate,
        rh_client_returnable: allocation.rhRtoz,
        rh_mk: allocation.rhMarketRate,
        rh_value: allocation.rhValue
      }))
    },
    clientValueDateAuto: {
      get () {
        return this.$store.state['clientProformaStore'].clientValueDateAuto
      },
      set (value) {
        this.$store.commit('clientProformaStore/setClientValueDateAuto', value)
        if (value) this.$store.commit('clientProformaStore/setClientValueDate', this.getClientValueDateByArrival)
      }
    },
    refiningFeeAuto: {
      get () {
        return this.$store.state['clientProformaStore'].refiningFeeAuto
      },
      set (value) {
        this.$store.commit('clientProformaStore/setRefiningFeeAuto', value)
        if (value) this.$store.commit('clientProformaStore/setRefiningFee', this.getTermsRefiningFeeTotal)
      }
    },
    processingFeeAuto: {
      get () {
        return this.$store.state['clientProformaStore'].processingFeeAuto
      },
      set (value) {
        this.$store.commit('clientProformaStore/setProcessingFeeAuto', value)
        if (value) this.$store.commit('clientProformaStore/setProcessingFee', this.getTermsTreatmentCharge)
      }
    },
    handlingFeeAuto: {
      get () {
        return this.$store.state['clientProformaStore'].handlingFeeAuto
      },
      set (value) {
        this.$store.commit('clientProformaStore/setHandlingFeeAuto', value)
        if (value) this.$store.commit('clientProformaStore/setHandlingFee', this.getTermsHandlingFee)
      }
    },
    assayFeeAuto: {
      get () {
        return this.$store.state['clientProformaStore'].assayFeeAuto
      },
      set (value) {
        this.$store.commit('clientProformaStore/setAssayFeeAuto', value)
        if (value) this.$store.commit('clientProformaStore/setAssayFee', this.getTermAssayFee)
      }
    },
    shreddingFeeAuto: {
      get () {
        return this.$store.state['clientProformaStore'].shreddingFeeAuto
      },
      set (value) {
        this.$store.commit('clientProformaStore/setShreddingFeeAuto', value)
        if (value) this.$store.commit('clientProformaStore/setShreddingFee', this.getTermShreddingFee)
      }
    },
    brokerageFeeAuto: {
      get () {
        return this.$store.state['clientProformaStore'].brokerageFeeAuto
      },
      set (value) {
        this.$store.commit('clientProformaStore/setBrokerageFeeAuto', value)
        if (value) this.$store.commit('clientProformaStore/setBrokerageFee', this.getTermBrokerageFee)
      }
    },
    ouncePayoutTypeAuto: {
      get () {
        return this.$store.state['clientProformaStore'].ouncePayoutTypeAuto
      },
      set (value) {
        this.$store.commit('clientProformaStore/setOuncePayoutTypeAuto', value)
        if (value) this.$store.commit('clientProformaStore/setOuncePayoutType', this.getTermsPayoutType)
      }
    },
    interestRateAuto: {
      get () {
        return this.$store.state['clientProformaStore'].interestRateAuto
      },
      set (value) {
        this.$store.commit('clientProformaStore/setInterestRateAuto', value)
        if (value) this.$store.commit('clientProformaStore/setInterestPercent', this.getTermInterestRate)
      }
    },
    ...mapState('clientProformaStore', [
      'advanceCalculatorHistory',
      'arrivalDate',
      'assayFee',
      'brokerageFee',
      'brokerageTerms',
      'brokeredRatePd',
      'brokeredRatePt',
      'brokeredRateRh',
      'clientValueDate',
      'consolidateFees',
      'containedPd',
      'containedPt',
      'containedRh',
      'convertersProcessed',
      'depositPercent',
      'expectedPaymentDate',
      'freightCharge',
      'handlingFee',
      'historyVersion',
      'interestPercent',
      'isLocked',
      'lotId',
      'lotName',
      'mixedCatalystWeight',
      'netOtherFees',
      'ouncePayoutType',
      'printPieces',
      'printWeight',
      'processingFee',
      'refiningFee',
      'shipDate',
      'shreddingFee',
      'yardId',
      'customerReference'
    ]),
    ...mapGetters('clientProformaStore', [
      'getAllocatedPd',
      'getAllocatedPt',
      'getAllocatedReturnablePd',
      'getAllocatedReturnablePt',
      'getAllocatedReturnableRh',
      'getAllocatedRh',
      'getAvailablePd',
      'getAvailablePt',
      'getAvailableRh',
      'getCleanedBrokerageTerms',
      'getClientValueDateByArrival',
      'getDaysOfInterest',
      'getDepositTotal',
      'getFinanceCharge',
      'getGrossMetalValue',
      'getGrossOutrightUnpaidValue',
      'getInternalPdToz',
      'getInternalPtToz',
      'getInternalRhToz',
      'getNetValue',
      'getNetValueLessBrokerage',
      'getRecommendedAdvance',
      'getSettlementAmountDue',
      'getSummaryReturnablePdToz',
      'getSummaryReturnablePtToz',
      'getSummaryReturnableRhToz',
      'getTermAssayFee',
      'getTermBrokerageFee',
      'getTermShreddingFee',
      'getTermsClientPaymentDays',
      'getTermsHandlingFee',
      'getTermsPayoutType',
      'getTermsRefiningFeeTotal',
      'getTermsTreatmentCharge',
      'getTermInterestRate',
      'getTotalFees',
      'getUnallocatedHedges',
      'getUnallocatedPd',
      'getUnallocatedPt',
      'getUnallocatedRh',
      'getAdvances',
      'totalAdvances',
      'totalAdvancesInterest',
      'settlementDue',
      'getFreightChargeResponsibilityPGM'
    ])
  },
  methods: {
    updateValues () {
      if (this.isLocked) return
      if (this.getFreightChargeResponsibilityPGM) {
        this.$store.commit('clientProformaStore/setFreightCharge', 0)
      }
    },
    async setOuncePayoutType (value) {
      this.$store.commit('clientProformaStore/setOuncePayoutType', value)
      if (!this.$store.state['clientProformaStore'].seedData) {
        await this.$store.dispatch('clientProformaStore/refreshDataFromLotData', { seedOnly: true })
      }
      this.$store.commit('clientProformaStore/updateBrokeredRates', this.ouncePayoutType)
    },
    addPurchasedOutrightCode (code) {
      this.$store.commit('clientProformaStore/addPurchasedOutrightCode', code)
      this.unitCode = null
    },
    historyLabelFormat (history) {
      let text = `${history.id}. ${moment(history.created_at).format('MM/DD/YYYY HH:mm:ss')} ${history.fx_currency}`
      if (this.advanceCalculatorHistory.length > 0 && history.id === this.advanceCalculatorHistory[0].id) text += ' (Latest)'
      return text
    },
    addSearchedHedge (hedge) {
      this.addHedge({
        id: hedge.id,
        ptAvailableToz: hedge.pt_available_toz,
        ptRate: hedge.pt_rate_per_toz_fx,
        pdAvailableToz: hedge.pd_available_toz,
        pdRate: hedge.pd_rate_per_toz_fx,
        rhAvailableToz: hedge.rh_available_toz,
        rhRate: hedge.rh_rate_per_toz_fx
      })
    },
    addBlankHedge () {
      const prefix = 'Live '
      function getNextCSequence (ids) {
        let cValue = 0
        for (const id of ids) {
          if (!(typeof id === 'string') || !id.startsWith(prefix)) continue
          const x = parseInt(id.slice(prefix.length))
          if (isNaN(x) || x < cValue) continue
          cValue = x
        }
        // Return the next C sequence value up to the target sequence (e.g., "C5")
        return prefix + (cValue + 1)
      }
      this.$store.commit('clientProformaStore/addHedgeToAllocations', {
        hedgeId: getNextCSequence(this.allocations.map(x => x.hedgeId)),
        ptAvailableToz: 0,
        ptToz: +this.getUnallocatedPt,
        ptMarketRate: 0,
        pdAvailableToz: 0,
        pdToz: +this.getUnallocatedPd,
        pdMarketRate: 0,
        rhAvailableToz: 0,
        rhToz: +this.getUnallocatedRh,
        rhMarketRate: 0
      })
    },
    addHedge (hedge) {
      if (this.allocations.find(alloc => alloc.hedgeId === hedge.id)) {
        this.$store.commit('setSnackbarWarning', 'Allocation for Hedge already exists.')
        return
      }
      this.$store.commit('clientProformaStore/addHedgeToAllocations', {
        hedgeId: hedge.id,
        ptToz: Math.min(+hedge.ptAvailableToz, +this.getUnallocatedPt),
        ptMarketRate: hedge.ptRate,
        pdToz: Math.min(+hedge.pdAvailableToz, +this.getUnallocatedPd),
        pdMarketRate: hedge.pdRate,
        rhToz: Math.min(+hedge.rhAvailableToz, +this.getUnallocatedRh),
        rhMarketRate: hedge.rhRate
      })
      this.isModified = true
    },
    updateAllocations (hedgeId, allocation) {
      let updatedAllocations = []
      for (const line of this.$store.getters['clientProformaStore/getAllocations']) {
        if (line.hedgeId === hedgeId) {
          line.ptToz = allocation.pt_returnable
          line.ptRate = allocation.pt_return_rate
          line.ptRtoz = allocation.pt_client_returnable
          line.ptMarketRate = allocation.pt_mk
          line.ptValue = allocation.pt_value
          line.pdToz = allocation.pd_returnable
          line.pdRate = allocation.pd_return_rate
          line.pdRtoz = allocation.pd_client_returnable
          line.pdMarketRate = allocation.pd_mk
          line.pdValue = allocation.pd_value
          line.rhToz = allocation.rh_returnable
          line.rhRate = allocation.rh_return_rate
          line.rhRtoz = allocation.rh_client_returnable
          line.rhMarketRate = allocation.rh_mk
          line.rhValue = allocation.rh_value
        }
        updatedAllocations.push(line)
      }
      this.$store.commit('clientProformaStore/setAllocations', updatedAllocations)
    },
    updateOutrightValue (material, key, value) {
      let updatedLines = []
      for (const line of this.$store.state['clientProformaStore'].purchasedOutright) {
        if (line.materialType === material) {
          line[key] = value
        }
        updatedLines.push(line)
      }
      this.$store.commit('clientProformaStore/setPurchasedOutright', updatedLines)
    },
    removeAllocation (allocation) {
      this.$store.commit('clientProformaStore/removeAllocation', allocation.hedgeId)
      this.isModified = true
    },
    save () {
      this.$store.dispatch('clientProformaStore/saveAdvanceCalculator').then(() => {
        this.isModified = false
      })
    },
    async initialize () {
      return Promise.all([
        this.$store.dispatch('clientProformaStore/fetchAdvanceCalculators'),
        this.$store.dispatch('clientProformaStore/fetchDesignatedHedges'),
        this.$store.dispatch('clientProformaStore/fetchAdvances')
      ])
    },
    setVersionFromHistoryById (id) {
      const history = this.advanceCalculatorHistory.find(h => h.id === id)
      if (history) {
        this.$store.commit('clientProformaStore/setContextFromHistory', history)
        return true
      }
      return false
    },
    refreshData () {
      return this.$store.dispatch('clientProformaStore/refreshDataFromLotData')
    }
  },
  beforeRouteLeave (_, __, next) {
    if (this.isModified) {
      if (!window.confirm('Leave without saving?')) {
        return
      }
    }
    next()
  },
  /*
   * Fetch the history, if no history then fetch the latest lot seed data
   */
  async mounted () {
    this.loading = true
    this.$store.commit('clientProformaStore/clearState')
    this.$store.commit('clientProformaStore/setLotId', this.$route.params.id)
    await this.initialize()
    if (this.advanceCalculatorHistory.length) {
      this.$store.commit('clientProformaStore/setContextFromHistory', this.advanceCalculatorHistory[0])
    } else {
      // if there is no history, then seed data
      await this.refreshData()
      this.$store.commit('clientProformaStore/setIsLocked', false)
    }
    this.loading = false
  }
}
</script>
